import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Button, Chip, CircularProgress, Tabs, Tab, Snackbar, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Menu, MenuItem,
} from '@mui/material';
import {
  ArrowBack, Edit, Refresh, MoreVert, OpenInNew, TrendingUp, TrendingDown,
} from '@mui/icons-material';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend,
} from 'recharts';
import { budgetService, Budget360, VsActual, VsActualRow, GroupBy, BudgetStatus } from '../../services/budgetService';
import { formatCurrency, formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { StatusDot, StatusTone, TableSurface, headerCellSx, bodyRowSx, ErrorState } from '../../components/common/pageKit';
import BudgetFormDialog from './BudgetFormDialog';

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const STATUS_TONE: Record<string, StatusTone> = {
  DRAFT: 'warning', SUBMITTED: 'info', APPROVED: 'info', ACTIVE: 'success', LOCKED: 'neutral', CLOSED: 'neutral', REJECTED: 'error',
};
// Allowed forward transitions surfaced as buttons (mirror of the backend guard).
const NEXT: Record<string, { label: string; to: BudgetStatus }[]> = {
  DRAFT: [{ label: 'Submit', to: 'SUBMITTED' }, { label: 'Activate', to: 'ACTIVE' }],
  SUBMITTED: [{ label: 'Approve', to: 'APPROVED' }, { label: 'Reject', to: 'REJECTED' }],
  APPROVED: [{ label: 'Activate', to: 'ACTIVE' }],
  ACTIVE: [{ label: 'Lock', to: 'LOCKED' }, { label: 'Close', to: 'CLOSED' }],
  LOCKED: [{ label: 'Unlock', to: 'ACTIVE' }, { label: 'Close', to: 'CLOSED' }],
  REJECTED: [{ label: 'Back to Draft', to: 'DRAFT' }],
  CLOSED: [],
};

const Kpi: React.FC<{ label: string; value: string; color?: string; sub?: React.ReactNode }> = ({ label, value, color, sub }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.65rem' }}>{label}</Typography>
    <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: color || 'text.primary', lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    {sub}
  </Box>
);

// ---- Reusable vs-actual table (account / cost center / department / project) ----
const VsActualTable: React.FC<{ data: VsActual | null; onDrill?: (r: VsActualRow) => void }> = ({ data, onDrill }) => {
  const c = useControlRoomColors();
  if (!data) return <Box sx={{ display: 'grid', placeItems: 'center', py: 5 }}><CircularProgress /></Box>;
  const rows = data.rows;
  return (
    <TableSurface>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Budget', 'Actual', 'Remaining', 'Utilisation', 'Forecast', 'Variance', ''].map((h, i) => (
                <TableCell key={h || i} sx={headerCellSx} align={i >= 1 && i <= 6 ? 'right' : 'left'}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const util = r.utilisation;
              const uc = util == null ? c.sub : util > 100 ? c.bad : util >= 90 ? c.warn : c.good;
              return (
                <TableRow key={`${r.groupBy}-${r.key}`} hover sx={{ ...(bodyRowSx as any), cursor: onDrill ? 'pointer' : 'default' }}
                  onClick={() => onDrill && onDrill(r)}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.code ? `${r.code} · ` : ''}{r.name}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.budget)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{formatCurrency(r.actual)}</TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: r.remaining < 0 ? c.bad : 'inherit' }}>{formatCurrency(r.remaining)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.75}>
                      <Box sx={{ width: 60, height: 6, borderRadius: 3, bgcolor: `${uc}22`, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.min(Math.max(util || 0, 0), 100)}%`, height: '100%', bgcolor: uc }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: uc, minWidth: 40, textAlign: 'right' }}>{util != null ? `${util}%` : '—'}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(r.forecast)}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" label={r.varianceLabel} sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: r.favourable ? `${c.good}1f` : `${c.bad}1f`, color: r.favourable ? c.good : c.bad }} />
                  </TableCell>
                  <TableCell align="right">{onDrill && r.accountId ? <OpenInNew sx={{ fontSize: 15, color: c.sub }} /> : null}</TableCell>
                </TableRow>
              );
            })}
            {data.totals && (
              <TableRow sx={{ '& td': { fontWeight: 800, borderTop: `2px solid ${c.line}` } }}>
                <TableCell>Total</TableCell>
                <TableCell align="right">{formatCurrency(data.totals.budget)}</TableCell>
                <TableCell align="right">{formatCurrency(data.totals.actual)}</TableCell>
                <TableCell align="right">{formatCurrency(data.totals.remaining)}</TableCell>
                <TableCell align="right" />
                <TableCell align="right">{formatCurrency(data.totals.forecast)}</TableCell>
                <TableCell align="right" colSpan={2}>{formatCurrency(data.totals.variance)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TableSurface>
  );
};

const Budget360Page: React.FC = () => {
  const { id } = useParams();
  const budgetId = Number(id);
  const navigate = useNavigate();
  const c = useControlRoomColors();
  const ct = useChartTokens();

  const [data, setData] = useState<Budget360 | null>(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);

  // dimension slices, fetched lazily
  const [ccData, setCcData] = useState<VsActual | null>(null);
  const [depData, setDepData] = useState<VsActual | null>(null);
  const [prjData, setPrjData] = useState<VsActual | null>(null);
  const [accData, setAccData] = useState<VsActual | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    budgetService.budget360(budgetId)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load budget'))
      .finally(() => setLoading(false));
  }, [budgetId]);
  useEffect(() => { load(); }, [load]);

  // Lazy-load slice for a dimension when its tab is first opened.
  const ensureSlice = useCallback((g: GroupBy) => {
    const setter = { account: setAccData, costCenter: setCcData, department: setDepData, project: setPrjData }[g];
    const cur = { account: accData, costCenter: ccData, department: depData, project: prjData }[g];
    if (cur) return;
    budgetService.vsActual(budgetId, g, true).then(setter as any).catch(() => {});
  }, [budgetId, accData, ccData, depData, prjData]);

  useEffect(() => {
    if (tab === 1) ensureSlice('account');
    if (tab === 2) ensureSlice('costCenter');
    if (tab === 3) ensureSlice('department');
  }, [tab, ensureSlice]);

  const drillAccount = (r: VsActualRow) => { if (r.accountId) navigate(`/finance/ledger?account=${r.accountId}`); };

  const transition = async (to: BudgetStatus) => {
    setMenuEl(null);
    try { await budgetService.update(budgetId, { status: to }); setToast(`Budget → ${to}`); load(); }
    catch (e: any) { setToast(e.message || 'Transition failed'); }
  };

  const h = data?.header;
  const monthData = useMemo(() => (data?.monthly || []).map((m) => ({ ...m, variance: Math.round((m.budget - m.actual) * 100) / 100 })), [data]);

  if (loading && !data) return <Box sx={{ display: 'grid', placeItems: 'center', height: '60vh' }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><ErrorState message={error} onRetry={load} /></Box>;
  if (!h) return null;

  const overBudget = h.forecast > h.totalBudget;
  const varianceRows = [...(data?.accounts || [])].sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

  return (
    <Box sx={{ bgcolor: c.bg, minHeight: '100%' }}>
      {/* Sticky header offset for the app shell */}
      <Box sx={{ position: 'sticky', top: { xs: 32, sm: 64 }, zIndex: 3, bgcolor: c.bg, borderBottom: `1px solid ${c.line}`, px: 3, pt: 2, pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate('/finance/budgets')}><ArrowBack /></IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>{h.name}</Typography>
                <Chip size="small" label={h.budgetType} sx={{ height: 20, fontWeight: 700, bgcolor: `${c.accent}14`, color: c.accent }} />
                <StatusDot label={h.status} tone={STATUS_TONE[h.status] || 'neutral'} />
                {h.version > 1 && <Chip size="small" label={`v${h.version}`} variant="outlined" sx={{ height: 20 }} />}
              </Stack>
              <Typography variant="caption" color="text.secondary">{h.financialYear}{h.owner ? ` · Owner ${h.owner}` : ''}{h.company ? ` · ${h.company}` : ''}{h.plant ? ` · ${h.plant}` : ''}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
            <Button size="small" variant="outlined" startIcon={<Edit />} onClick={() => setEditOpen(true)} disabled={h.status === 'CLOSED' || h.status === 'LOCKED'}>Edit</Button>
            {(NEXT[h.status] || []).length > 0 && <>
              <Button size="small" variant="contained" endIcon={<MoreVert />} onClick={(e) => setMenuEl(e.currentTarget)}>Actions</Button>
              <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}>
                {(NEXT[h.status] || []).map((n) => <MenuItem key={n.to} onClick={() => transition(n.to)}>{n.label}</MenuItem>)}
              </Menu>
            </>}
          </Stack>
        </Stack>
        {/* KPI row */}
        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mt: 1.5, rowGap: 1 }}>
          <Kpi label="Total Budget" value={formatIndianNumber(h.totalBudget)} />
          <Kpi label="Actual" value={formatIndianNumber(h.actual)} color={c.accent} />
          <Kpi label="Remaining" value={formatIndianNumber(h.remaining)} color={h.remaining >= 0 ? c.good : c.bad} />
          <Kpi label="Utilisation" value={h.utilisation != null ? `${h.utilisation}%` : '—'} color={(h.utilisation || 0) > 100 ? c.bad : c.text} />
          <Kpi label="Forecast @ Completion" value={formatIndianNumber(h.forecast)} color={overBudget ? c.warn : c.good}
            sub={<Stack direction="row" spacing={0.25} alignItems="center">{overBudget ? <TrendingUp sx={{ fontSize: 13, color: c.warn }} /> : <TrendingDown sx={{ fontSize: 13, color: c.good }} />}<Typography variant="caption" sx={{ color: overBudget ? c.warn : c.good, fontWeight: 700 }}>{overBudget ? 'Trending over' : 'On track'}</Typography></Stack>} />
          <Kpi label="Variance" value={formatIndianNumber(h.variance)} color={h.variance >= 0 ? c.good : c.bad} />
          <Kpi label="FY Elapsed" value={`${Math.round(h.elapsedFraction * 100)}%`} />
        </Stack>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}>
          {['Overview', 'Accounts', 'By Cost Center', 'By Department', 'Monthly Trend', 'Variance', 'Versions', 'Audit'].map((t) => <Tab key={t} label={t} />)}
        </Tabs>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Overview */}
        {tab === 0 && (
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 8 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Monthly Plan vs Actual</Typography>
                <MonthlyChart data={monthData} ct={ct} c={c} />
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel, height: '100%' }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Largest Variances</Typography>
                <Stack spacing={1}>
                  {varianceRows.slice(0, 6).map((r) => (
                    <Stack key={r.key} direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1, borderRadius: 2, border: `1px solid ${c.line}` }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.utilisation != null ? `${r.utilisation}% utilised` : '—'}</Typography>
                      </Box>
                      <Chip size="small" label={formatIndianNumber(r.variance)} sx={{ fontWeight: 700, bgcolor: r.favourable ? `${c.good}1f` : `${c.bad}1f`, color: r.favourable ? c.good : c.bad }} />
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Accounts */}
        {tab === 1 && (
          <>
            <VsActualTable data={accData} onDrill={drillAccount} />
            {accData && <MonthlyGrid rows={accData.rows} c={c} />}
          </>
        )}

        {tab === 2 && <VsActualTable data={ccData} />}
        {tab === 3 && <VsActualTable data={depData} />}

        {/* Monthly Trend */}
        {tab === 4 && (
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Budget vs Actual by Month (Apr-based FY)</Typography>
            <MonthlyChart data={monthData} ct={ct} c={c} height={380} />
          </Paper>
        )}

        {/* Variance */}
        {tab === 5 && (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  {['Account', 'Budget', 'Actual', 'Variance', 'Variance %', 'Assessment'].map((hh, i) => <TableCell key={hh} sx={headerCellSx} align={i >= 1 && i <= 4 ? 'right' : 'left'}>{hh}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {varianceRows.map((r) => (
                    <TableRow key={r.key} hover sx={bodyRowSx as any}>
                      <TableCell><Typography variant="body2" sx={{ fontWeight: 700 }}>{r.accountCode ? `${r.accountCode} · ` : ''}{r.name}</Typography></TableCell>
                      <TableCell align="right">{formatCurrency(r.budget)}</TableCell>
                      <TableCell align="right">{formatCurrency(r.actual)}</TableCell>
                      <TableCell align="right" sx={{ color: r.favourable ? c.good : c.bad, fontWeight: 700 }}>{formatCurrency(r.variance)}</TableCell>
                      <TableCell align="right">{r.variancePct != null ? `${r.variancePct}%` : '—'}</TableCell>
                      <TableCell><StatusDot label={r.varianceLabel} tone={r.favourable ? 'success' : 'error'} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}

        {/* Versions */}
        {tab === 6 && (
          <TableSurface>
            <TableContainer>
              <Table size="small">
                <TableHead><TableRow>
                  {['Version', 'Reason', 'Total Amount', 'By', 'When'].map((hh, i) => <TableCell key={hh} sx={headerCellSx} align={i === 2 ? 'right' : 'left'}>{hh}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {(data?.versions || []).map((v) => (
                    <TableRow key={v.id} hover sx={bodyRowSx as any}>
                      <TableCell><Chip size="small" label={`v${v.version}`} variant="outlined" /></TableCell>
                      <TableCell>{v.reason || '—'}</TableCell>
                      <TableCell align="right">{formatCurrency(v.total_amount)}</TableCell>
                      <TableCell>{v.createdByName || (v.created_by ? `#${v.created_by}` : '—')}</TableCell>
                      <TableCell>{v.created_at ? new Date(v.created_at).toLocaleString('en-IN') : '—'}</TableCell>
                    </TableRow>
                  ))}
                  {data && !data.versions.length && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: c.sub }}>No version history.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        )}

        {/* Audit */}
        {tab === 7 && data && (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px solid ${c.line}`, bgcolor: c.panel, maxWidth: 620 }}>
            <Typography sx={{ fontWeight: 700, mb: 2 }}>Audit Trail</Typography>
            <Stack spacing={1.5}>
              <AuditRow label="Created by" value={data.audit.createdByName || (data.audit.createdBy ? `#${data.audit.createdBy}` : '—')} when={data.audit.createdAt} />
              <AuditRow label="Approved by" value={data.audit.approvedByName || (data.audit.approvedBy ? `#${data.audit.approvedBy}` : 'Not yet approved')} when={data.audit.approvedAt} />
              <AuditRow label="Revisions" value={`${data.audit.versionsCount} version(s)`} />
              <AuditRow label="Current status" value={h.status} />
              {h.notes && <AuditRow label="Notes" value={h.notes} />}
            </Stack>
          </Paper>
        )}
      </Box>

      <BudgetFormDialog open={editOpen} editBudgetId={budgetId} onClose={() => setEditOpen(false)} onSaved={() => { setToast('Budget updated'); setAccData(null); setCcData(null); setDepData(null); load(); }} />
      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const AuditRow: React.FC<{ label: string; value: string; when?: string | null }> = ({ label, value, when }) => (
  <Stack direction="row" justifyContent="space-between" sx={{ pb: 1, borderBottom: '1px dashed', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Box sx={{ textAlign: 'right' }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
      {when && <Typography variant="caption" color="text.secondary">{new Date(when).toLocaleString('en-IN')}</Typography>}
    </Box>
  </Stack>
);

const MonthlyChart: React.FC<{ data: any[]; ct: any; c: any; height?: number }> = ({ data, ct, c, height = 260 }) => (
  <Box sx={{ height, width: '100%' }}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -8 }}>
        <CartesianGrid {...gridProps(ct)} />
        <XAxis dataKey="month" {...axisProps(ct)} />
        <YAxis {...axisProps(ct)} width={54} tickFormatter={(v) => formatIndianNumber(v)} />
        <RTooltip {...tooltipStyle(ct)} formatter={(v: any, n: any) => [formatCurrency(Number(v)), n]} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="budget" name="Budget" fill={ct.series(0)} radius={[3, 3, 0, 0]} maxBarSize={26} />
        <Bar dataKey="actual" name="Actual" fill={ct.series(1)} radius={[3, 3, 0, 0]} maxBarSize={26} />
        <Line type="monotone" dataKey="actual" name="Actual trend" stroke={ct.series(1)} strokeWidth={2} dot={false} legendType="none" />
      </ComposedChart>
    </ResponsiveContainer>
  </Box>
);

// Monthly heat grid: months as columns, accounts as rows, actual vs budget shading.
const MonthlyGrid: React.FC<{ rows: VsActualRow[]; c: any }> = ({ rows, c }) => (
  <Box sx={{ mt: 2 }}>
    <Typography sx={{ fontWeight: 700, mb: 1 }}>Monthly Allocation Grid (budget → actual)</Typography>
    <TableSurface>
      <TableContainer>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ ...headerCellSx, position: 'sticky', left: 0, bgcolor: c.panel, zIndex: 1 }}>Account</TableCell>
            {MONTHS.map((m) => <TableCell key={m} sx={headerCellSx} align="right">{m}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.key} hover sx={bodyRowSx as any}>
                <TableCell sx={{ position: 'sticky', left: 0, bgcolor: c.panel, fontWeight: 700, whiteSpace: 'nowrap' }}>{r.accountCode || r.code} · {r.name}</TableCell>
                {MONTHS.map((m, mi) => {
                  const b = r.monthlyBudget[mi] || 0; const a = r.monthlyActual[mi] || 0;
                  const over = a > b && b > 0;
                  return (
                    <TableCell key={m} align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.72rem', color: over ? c.bad : 'inherit', bgcolor: a > 0 ? `${over ? c.bad : c.good}0d` : 'transparent' }}>
                      {a > 0 ? formatIndianNumber(a) : (b > 0 ? formatIndianNumber(b) : '—')}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </TableSurface>
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Shows posted actual where available (green), else planned budget; red where actual exceeds plan.</Typography>
  </Box>
);

export default Budget360Page;
